/**
 * Navigation state for the Key Bindings TomeWindow (Stage 2 / Round 5A).
 *
 * Tracks how Key Bindings was opened ('gameMenu' | 'settings' | 'direct') so:
 * - When opened from the Esc menu (GameMenu), the header shows "← Game menu"
 *   and pressing Esc (when not capturing a key or confirming a reset) steps
 *   back to the Esc menu and returns focus to the "Key Bindings" button.
 * - When opened from Settings → Controls ("Open Key Bindings"), closing returns
 *   focus to the "Open Key Bindings" button.
 */

import { useSyncExternalStore } from 'react';
import { windowBridge } from '../../framework/windowBridge';
import { gameMenuStore } from '../../HUD/gameMenu/gameMenuStore';

export type KeyBindingsOrigin = 'gameMenu' | 'settings' | 'direct';

let currentOrigin: KeyBindingsOrigin = 'direct';
let escapeIntercepted = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

function focusSelectorSoon(selector: string): void {
  if (typeof document === 'undefined') return;
  const attempt = () => {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) el.focus();
  };
  attempt();
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(attempt);
  }
}

export const keyBindingsNavigationStore = {
  getOrigin: (): KeyBindingsOrigin => currentOrigin,
  setOrigin: (origin: KeyBindingsOrigin): void => {
    if (currentOrigin === origin) return;
    currentOrigin = origin;
    notify();
  },
  isEscapeIntercepted: (): boolean => escapeIntercepted,
  setEscapeIntercepted: (active: boolean): void => {
    escapeIntercepted = active;
  },
  openFromGameMenu: (): void => {
    currentOrigin = 'gameMenu';
    notify();
    gameMenuStore.close();
    windowBridge.open('keyBindings');
  },
  openFromSettings: (): void => {
    currentOrigin = 'settings';
    notify();
    windowBridge.open('keyBindings');
  },
  toggleDirect: (): void => {
    currentOrigin = 'direct';
    notify();
    windowBridge.toggle('keyBindings');
  },
  returnToGameMenu: (): void => {
    currentOrigin = 'direct';
    notify();
    windowBridge.close('keyBindings');
    gameMenuStore.open();
    focusSelectorSoon('[data-testid="game-menu-key-bindings"]');
  },
  restoreFocusOnClose: (originAtClose: KeyBindingsOrigin = currentOrigin): void => {
    currentOrigin = 'direct';
    notify();
    if (originAtClose === 'gameMenu') {
      focusSelectorSoon('[data-testid="game-menu-key-bindings"]');
    } else if (originAtClose === 'settings') {
      focusSelectorSoon('[data-testid="open-key-bindings-button"]');
    }
  },
  resetForTests: (): void => {
    currentOrigin = 'direct';
    escapeIntercepted = false;
    notify();
  },
  subscribe: (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

export function useKeyBindingsOrigin(): KeyBindingsOrigin {
  return useSyncExternalStore(
    keyBindingsNavigationStore.subscribe,
    keyBindingsNavigationStore.getOrigin,
    keyBindingsNavigationStore.getOrigin,
  );
}
